import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore';
import { supabase } from '@/lib/supabase';
import { Disclaimer } from '@/components/ui/Disclaimer';

// ════════════════════════════════════════════════════════════════════
// /start — German onboarding (15 seconds, no signup)
//
// Mirrors Start.tsx 1:1 with German strings. Keeps:
//   · iOS-only "TT.MM.JJJJ" date placeholder overlay (iOS shows blank
//     date inputs natively)
//   · ?prompt= forwarding to /chat so question cards from the German
//     landing carry through onboarding to an auto-sent first message
// ════════════════════════════════════════════════════════════════════

function todayISO(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function twoYearsAgoISO(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 2);
  d.setDate(d.getDate() - 30);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default function StartDE() {
  const navigate = useNavigate();
  const signInAnonymously = useAuthStore((s) => s.signInAnonymously);

  const [searchParams] = useSearchParams();
  const incomingPrompt = searchParams.get('prompt');

  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isIOS = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /iPhone|iPad|iPod/.test(navigator.userAgent);
  }, []);

  const canSubmit = name.trim().length > 0 && dob.length > 0 && !submitting;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);

    try {
      const { error: signInErr, user } = await signInAnonymously();
      if (signInErr || !user) {
        throw new Error(signInErr ?? 'Konnte keine Sitzung starten. Bitte versuche es erneut.');
      }

      const { error: babyErr } = await supabase
        .from('babies')
        .insert({
          user_id: user.id,
          name: name.trim(),
          date_of_birth: dob,
          is_active: true,
        });
      if (babyErr) {
        throw new Error(babyErr.message ?? 'Konnte dein Baby nicht speichern. Bitte versuche es erneut.');
      }

      const dest = incomingPrompt
        ? `/chat?prompt=${encodeURIComponent(incomingPrompt)}`
        : '/chat';
      navigate(dest, { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Etwas ist schiefgelaufen.';
      setError(msg);
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-[100dvh] bg-cream flex flex-col">
      <nav className="mx-auto w-full max-w-6xl px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shadow-sm">
            <span className="text-white text-base font-display font-semibold">A</span>
          </div>
          <span className="font-display text-xl font-semibold text-slate tracking-tight">
            Alma
          </span>
        </Link>
        <Link to="/login" className="btn-ghost text-sm">
          Ich habe schon ein Konto
        </Link>
      </nav>

      <main className="flex-1 flex items-center justify-center px-6 pb-10">
        <div className="w-full max-w-md">
          <div
            aria-hidden
            className="pointer-events-none fixed inset-0 -z-10"
            style={{
              background:
                'radial-gradient(60rem 40rem at 85% -10%, rgba(232,164,160,0.16) 0%, transparent 60%), radial-gradient(50rem 35rem at -10% 110%, rgba(143,181,158,0.16) 0%, transparent 55%)',
            }}
          />

          <div className="text-center mb-8">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700 mb-3">
              Fünfzehn Sekunden
            </div>
            <h1 className="font-display text-4xl sm:text-[44px] font-semibold tracking-tight text-slate leading-[1.05]">
              Erzähl mir von deinem Baby.
            </h1>
            <p className="mt-4 text-base text-slate-muted leading-relaxed">
              Zwei Fragen. Keine Anmeldung. Alma stimmt jede Antwort auf das genaue
              Alter deines Babys ab — deshalb ist das wichtig.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="rounded-2xl bg-white border border-primary-100 p-6 sm:p-7 shadow-sm space-y-5"
          >
            <div>
              <label
                htmlFor="baby-name"
                className="block text-sm font-medium text-slate mb-2"
              >
                Name deines Babys
              </label>
              <input
                id="baby-name"
                type="text"
                inputMode="text"
                autoComplete="off"
                autoFocus
                placeholder="z. B. Lena"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={submitting}
                maxLength={40}
                className="w-full rounded-xl border border-primary-100 bg-cream/40 px-4 py-3 text-base text-slate placeholder:text-slate-muted/60 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition disabled:opacity-50"
              />
              <p className="mt-1.5 text-[12px] text-slate-muted">
                Der Vorname reicht — Alma nutzt ihn, damit Antworten persönlich werden.
              </p>
            </div>

            <div>
              <label
                htmlFor="baby-dob"
                className="block text-sm font-medium text-slate mb-2"
              >
                Geburtsdatum
              </label>
              <div className="relative">
                <input
                  id="baby-dob"
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  disabled={submitting}
                  min={twoYearsAgoISO()}
                  max={todayISO()}
                  className="w-full rounded-xl border border-primary-100 bg-cream/40 px-4 py-3 text-base text-slate focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition disabled:opacity-50"
                />
                {!dob && isIOS && (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-slate-muted/50"
                  >
                    TT.MM.JJJJ
                  </span>
                )}
              </div>
              <p className="mt-1.5 text-[12px] text-slate-muted">
                Alma ist aktuell auf Babys von 0–24 Monaten abgestimmt.
              </p>
            </div>

            {error && (
              <div className="rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full rounded-xl bg-primary text-white font-medium text-base py-3.5 hover:bg-primary-600 active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
            >
              {submitting ? 'Einen Moment…' : 'Chat starten →'}
            </button>

            <p className="text-center text-[12px] text-slate-muted">
              3 Fragen kostenlos. Keine Karte. Keine E-Mail.
            </p>
          </form>

          <div className="mt-8">
            <Disclaimer className="text-center text-slate-muted text-[11px] leading-relaxed" />
          </div>
        </div>
      </main>
    </div>
  );
}
